import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 *
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  if (!cartData || !productsData) return [];
  return cartData
    .map((CartItem) => {
      const product = productsData.find((p) => p._id === CartItem.productId);
      if (!product) return null;
      return {
        ...product,
        qty: CartItem.qty,
        productId: CartItem.productId,
      };
    })
    .filter(Boolean);
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  return items.reduce((acc, item) => {
    return acc + item.cost * item.qty;
  }, 0);
};

export const getTotalItems = (items = []) => {
  return items.reduce((acc, item) => acc + item.qty, 0);
};


/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 *
 * @param {Number} value
 *    Current quantity of product in cart
 *
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 *
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 *
 * @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 *
 */
const ItemQuantity = ({ value, handleAdd, handleDelete,readonly = false, }) => {
  if (readonly) {
    return (
      <Box data-testid="item-qty">
        Qty: {value}
      </Box>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton size="small" className="quantity-btn" onClick={handleDelete}>
        <RemoveOutlined />
      </IconButton>
      <Box className="quantity-value" data-testid="item-qty">
        {value}
      </Box>

      <IconButton size="small" className="quantity-btn" onClick={handleAdd}>
        <AddOutlined />
      </IconButton>
    </Stack>
  );
};

/**
 * Component to display the Cart view
 *
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 *
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 *
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 *
 *
 */
const Cart = ({ products, items = [], addToCart,readonly = false, }) => {
  const history = useHistory();

  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }

  return (
    <Box className="cart">
      {/* Cart Header */}
      <Box
        display="flex"
        alignItems="center"
        padding="1rem"
        borderBottom="1px solid #ddd"
      >
        <ShoppingCart style={{ marginRight: "8px" }} />
        <Box fontWeight="600">My Cart</Box>
      </Box>

      {/* Cart Items */}
      {items.map((item) => (
        <Box
          key={item.productId}
          display="flex"
          alignItems="flex-start"
          padding="1rem"
          className="cart-item"
        >
          <Box className="image-container">
            <img src={item.image} alt={item.name} width="100%" height="100%" />
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="6rem"
            paddingX="1rem"
            width="100%"
          >
            <div className="cart-item-name">{item.name}</div>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <ItemQuantity
                value={item.qty}
                readonly={readonly}
                handleAdd={() => addToCart(item.productId, item.qty + 1)}
                handleDelete={() => addToCart(item.productId, item.qty - 1)}
              />

              <Box fontWeight="700">${item.cost}</Box>
            </Box>
          </Box>
        </Box>
      ))}

      {/* Order Total */}
      <Box
        padding="1rem"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        borderTop="1px solid #ddd"
      >
        <Box>Order total</Box>
        <Box fontWeight="700" fontSize="1.5rem" data-testid="cart-total">
          ${getTotalCartValue(items)}
        </Box>
      </Box>

      {/* Checkout Button */}
      <Box padding="1rem">
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => history.push("/checkout")}
        >
          Checkout
        </Button>
      </Box>
    </Box>
  );
};

export default Cart;
